import scrollLock from 'scroll-lock';
import throttle from 'lodash.throttle';
import { gsap } from 'gsap';
import { getKeyboardFocusableElements } from '../utils';

export default class Header {
	static init(e) {
		const els = e.detail.element.querySelectorAll('[data-header]');
		els.forEach((el) => new this(el));
	}

	constructor(el) {
		this.el = el;

		this.menuOpen = false;
		this.mayStick = false;
		this.scrolledUp = false;
		this.headerSticky = false;
		this.headerAnimating = false;

		this.hamburger = this.el.querySelector('[data-hamburger]');
		this.hamburger.addEventListener('click', () => {
			this.menuOpen ? this.close() : this.open();
		});

		this.subMenuToggles = this.el.querySelectorAll('[data-submenu-toggle]');

		const handleSubMenuClick = (e, toggle) => {
			e.preventDefault();
			this.openSubMenu(toggle);
		};

		const resizeObserver = new ResizeObserver(() => {
			const isMobile = window.innerWidth <= 768;

			this.subMenuToggles.forEach((toggle) => {
				if (isMobile) {
					if (!toggle.boundHandler) {
						const boundHandler = (e) => handleSubMenuClick(e, toggle);
						toggle.boundHandler = boundHandler;
						toggle.addEventListener('click', boundHandler);
					}
				} else {
					if (toggle.boundHandler) {
						toggle.removeEventListener('click', toggle.boundHandler);
						delete toggle.boundHandler;
					}
				}
			});

			if (!isMobile) {
				this.menuOpen = false;
				scrollLock.enablePageScroll();
			}
		});

		resizeObserver.observe(document.body);

		document.addEventListener(
			'scroll',
			throttle((e) => this.checkScroll(), 100)
		);

		this.prevScroll = 0;
		this.checkScroll();

		var stickLoop = () => {
			if (this.mayStick) {
				if (this.scrolledUp) {
					this.stickHeader();
				} else {
					this.unStickHeader();
				}
				this.prevScroll = window.scrollY;
			} else {
				this.swapHeader();
			}
			requestAnimationFrame(stickLoop);
		};
		stickLoop();

		const focusableElements = getKeyboardFocusableElements(this.el);
		if (focusableElements.length) {
			this.firstElement = focusableElements[0];
			this.lastElement = focusableElements[focusableElements.length - 1];
		}

		window.addEventListener('keydown', (e) => {
			if (this.menuOpen) {
				if (e.key === 'Escape') {
					this.close();
				}

				if (e.key === 'Tab' || e.key === 9) {
					if (e.shiftKey) {
						/* shift + tab */
						if (document.activeElement === this.firstElement) {
							this.lastElement.focus();
							e.preventDefault();
						}
					} /* tab */ else {
						if (document.activeElement === this.lastElement) {
							this.firstElement.focus();
							e.preventDefault();
						}
					}
				}
			}
		});

		if (this.el) {
			const submenus = this.el.querySelectorAll('[data-submenu]');
			const submenusHeights = [];

			submenus.forEach((submenu) => {
				const submenuHeight = submenu.offsetHeight;
				submenusHeights.push(submenuHeight);
			});
			const maxHeight = Math.max(...submenusHeights);
			this.el.style.setProperty('--navHeight', maxHeight + 40 + 'px');
		} else {
			this.el.style.setProperty('--navHeight', '0px');
		}
	}

	open() {
		if (this.menuOpen) {
			return;
		}
		this.menuOpen = true;
		document.body.classList.add('body--menu-open');

		this.hamburger.setAttribute('aria-expanded', true);
		this.hamburger.setAttribute('aria-label', 'close menu');
		scrollLock.disablePageScroll();
	}

	close() {
		if (!this.menuOpen) {
			return;
		}
		this.menuOpen = false;
		document.body.classList.remove('body--menu-open');

		this.hamburger.setAttribute('aria-expanded', false);
		this.hamburger.setAttribute('aria-label', 'open menu');
		this.hamburger.focus();

		scrollLock.enablePageScroll();
	}

	checkScroll() {
		window.scrollY > 0 ? (this.mayStick = true) : (this.mayStick = false);

		if (this.prevScroll > window.scrollY) {
			this.scrolledUp = true;
		}
		if (this.prevScroll < window.scrollY) {
			this.scrolledUp = false;
		}

		this.prevScroll = window.scrollY;
	}

	stickHeader() {
		if (this.menuOpen || this.headerAnimating || this.headerSticky) {
			return;
		}

		this.headerAnimating = true;
		this.headerSticky = true;
		document.body.classList.add('body--menu-sticky');

		gsap.fromTo(
			this.el,
			{
				translateY: '0%',
			},
			{
				translateY: '100%',
				duration: 0.5,
				ease: 'sine.inOut',
				onComplete: () => {
					this.headerAnimating = false;
				},
			}
		);
	}

	unStickHeader() {
		if (this.menuOpen || this.headerAnimating || !this.headerSticky) {
			return;
		}

		this.headerAnimating = true;

		gsap.fromTo(
			this.el,
			{
				translateY: '100%',
			},
			{
				translateY: '0%',
				duration: 0.5,
				onComplete: () => {
					document.body.classList.remove('body--menu-sticky');
					this.headerAnimating = false;
					this.headerSticky = false;
				},
			}
		);
	}

	swapHeader() {
		if (this.menuOpen || this.headerAnimating || !this.headerSticky) {
			return;
		}

		this.headerAnimating = true;
		gsap.fromTo(
			this.el,
			{
				translateY: '100%',
			},
			{
				translateY: '0%',
				duration: 0,
				onComplete: () => {
					document.body.classList.remove('body--menu-sticky');

					gsap.fromTo(
						this.el,
						{
							opacity: 0,
						},
						{
							opacity: 1,
							duration: 0,
							onComplete: () => {
								this.headerAnimating = false;
								this.headerSticky = false;
							},
						}
					);
				},
			}
		);
	}

	openSubMenu(toggle) {
		this.subMenuToggles.forEach((otherToggle) => {
			if (otherToggle !== toggle && otherToggle.classList.contains('is-open')) {
				otherToggle.classList.remove('is-open');
				otherToggle.setAttribute('aria-expanded', false);
				otherToggle.nextElementSibling.classList.remove('is-open');
			}
		});

		if (toggle.classList.contains('is-open')) {
			toggle.setAttribute('aria-expanded', false);
			toggle.classList.remove('is-open');
			toggle.nextElementSibling.classList.remove('is-open');
		} else {
			toggle.setAttribute('aria-expanded', true);
			toggle.classList.add('is-open');
			toggle.nextElementSibling.classList.add('is-open');
		}
	}
}
